import React, { useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowRight } from "lucide-react";
import defaultImageUrl from "@/assets/default-menu-item.webp";
import { useMenuItem } from "@/query/useMenuItem";

export const MenuItemViewer: React.FC = () => {
  const { t } = useTranslation();
  const { restaurantId, itemId } = useParams();
  // const getMenuItemById = useMenuViewerStore((state) => state.getMenuItemById);
  const { data: menuItem } = useMenuItem(itemId!);
  const navigate = useNavigate();

  const formattedPrice = useMemo(() => {
    if (!menuItem) return "";
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency: "ILS",
    }).format(menuItem.price);
  }, [menuItem]);

  if (!menuItem) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="animate-spin" size={24} />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-white text-gray-800 max-w-4xl mx-auto">
      <div className="relative w-full h-64 sm:h-96 overflow-hidden">
        <img
          src={menuItem.imageUrl ?? defaultImageUrl}
          alt={menuItem.name}
          className="w-full h-full object-cover object-center"
        />
        <Button
          variant="ghost"
          onClick={() => navigate(`/menu/${restaurantId}`)}
          className="absolute top-4 right-4 bg-white/80 hover:bg-white rounded-full p-2 shadow-md"
          aria-label="Back"
        >
          <ArrowRight
            style={{ height: "2rem", width: "2rem" }}
            className="text-gray-900"
          />
        </Button>
      </div>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 py-10 space-y-6">
        <div className="flex flex-col gap-3">
          <div className="flex flex-row justify-between gap-4">
            <h1 className="text-4xl font-bold tracking-tight sm:order-1">
              {menuItem.name}
            </h1>
            <span className="text-2xl font-semibold text-primary sm:order-2">
              {formattedPrice}
            </span>
          </div>
          <p className="text-base leading-relaxed text-gray-600">
            {menuItem.description}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {!menuItem.isAvailable && (
            <Badge variant="destructive">{t("menuItem.unavailable")}</Badge>
          )}

          {menuItem.calories && (
            <Badge variant="outline">{menuItem.calories} kcal</Badge>
          )}

          {menuItem.preparationTime && (
            <Badge variant="outline">{menuItem.preparationTime} min</Badge>
          )}

          {menuItem.allergens && (
            <Badge variant="outline">Allergens: {menuItem.allergens}</Badge>
          )}
        </div>

        {menuItem.options && menuItem.options.length > 0 && (
          <div className="pt-6">
            <Separator className="mb-4" />
            <h2 className="text-lg font-semibold mb-2">Options</h2>
            <ul className="space-y-2">
              {menuItem.options.map((opt) => (
                <li key={opt.name} className="flex justify-between text-gray-700">
                  <span>{opt.name}</span>
                  <span className="text-sm">+${opt.price.toFixed(2)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </main>
  );
};

export default MenuItemViewer;
